/* Hash-only first-party matching. Embed copies are generated; never store raw contact data. */
(function installMatchingIdentity(root) {
  'use strict';
  if (!root || root.LVMMatchingIdentity) return;
  const KEY = 'lvm_matching_identity_v1';
  const TTL = 180 * 86400000;
  const KEYS = ['em', 'ph', 'fn', 'ln', 'ct', 'zp', 'country', 'external_id'];
  let memory = null, generation = 0, captureSequence = 0;
  const embedded = () => { try { return root.parent && root.parent !== root; } catch (_) { return true; } };
  function normalize(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    if (typeof value.capturedAt !== 'number' || typeof value.expiresAt !== 'number') return null;
    const capturedAt = value.capturedAt, expiresAt = value.expiresAt, now = Date.now();
    if (value.version !== 1 || !Number.isFinite(capturedAt) || !Number.isFinite(expiresAt)
      || capturedAt > now + 300000 || capturedAt <= 0 || expiresAt <= now
      || expiresAt <= capturedAt || expiresAt - capturedAt > TTL) return null;
    const values = {};
    KEYS.forEach(key => { if (typeof value.values?.[key] === 'string' && /^[a-f0-9]{64}$/.test(value.values[key])) values[key] = value.values[key]; });
    return Object.keys(values).length ? {version: 1, capturedAt, expiresAt, values} : null;
  }
  function cookieRead() {
    try {
      const part = root.document.cookie.split(';').map(x => x.trim()).find(x => x.startsWith(KEY + '='));
      return part ? normalize(JSON.parse(decodeURIComponent(part.slice(KEY.length + 1)))) : null;
    } catch (_) { return null; }
  }
  function get() {
    const candidates = [normalize(memory)];
    if (!embedded()) {
      try { candidates.push(normalize(JSON.parse(root.localStorage.getItem(KEY)))); } catch (_) { /* cookie fallback */ }
      candidates.push(cookieRead());
    }
    const value = candidates.filter(Boolean).sort((a,b) => b.capturedAt - a.capturedAt)[0];
    return value ? {...value, values: {...value.values}} : null;
  }
  function remember(value) {
    const record = normalize(value); if (!record) return null;
    memory = record;
    if (!embedded()) {
      const text = JSON.stringify(record);
      let stored = false;
      try { root.localStorage.setItem(KEY, text); stored = root.localStorage.getItem(KEY) === text; } catch (_) { /* cookie fallback */ }
      try {
        root.document.cookie = KEY + '=' + (stored ? '' : encodeURIComponent(text))
          + '; Max-Age=' + (stored ? 0 : Math.max(0,Math.floor((record.expiresAt - Date.now()) / 1000))) + '; Path=/; SameSite=Lax; Secure';
      } catch (_) { /* current lifecycle still works */ }
    }
    return get();
  }
  function clear() {
    memory = null; generation++; captureSequence++;
    if (embedded()) return; // The host can clear its domain; the iframe owns memory only.
    try { root.localStorage.removeItem(KEY); } catch (_) { /* unavailable */ }
    try { root.document.cookie = KEY + '=; Max-Age=0; Path=/; SameSite=Lax; Secure'; } catch (_) { /* unavailable */ }
  }
  async function capture(input = {}) {
    if (!root.crypto?.subtle || typeof root.TextEncoder !== 'function') return null;
    const ownGeneration = generation, ownSequence = ++captureSequence;
    const text = x => typeof x === 'string' ? x.trim().toLowerCase().slice(0,255) : '';
    const email = text(input.email);
    const phone = typeof input.metaPhone === 'string' && /^[1-9][0-9]{1,14}$/.test(input.metaPhone) ? input.metaPhone : '';
    const countryAliases = {uk:'gb',gbr:'gb','united kingdom':'gb','great britain':'gb',usa:'us','united states':'us'};
    const country = countryAliases[text(input.country)] || text(input.country);
    const raw = {
      em: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '', ph: phone,
      fn: text(input.firstName), ln: text(input.lastName), ct: text(input.city).replace(/[0-9\s().-]/g,''),
      zp: text(input.postcode).replace(/\s/g,'').split('-')[0], country: /^[a-z]{2}$/.test(country) ? country : '',
      external_id: text(input.externalId)
    };
    const values = {};
    await Promise.all(Object.entries(raw).filter(([,value]) => value).map(async ([key,value]) => {
      const digest = await root.crypto.subtle.digest('SHA-256', new root.TextEncoder().encode(value));
      values[key] = Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2,'0')).join('');
    }));
    if (generation !== ownGeneration || captureSequence !== ownSequence || !Object.keys(values).length) return null;
    const prior = get();
    const changedSubject = (values.em && prior?.values.em && values.em !== prior.values.em)
      || (!values.em && values.ph && prior?.values.ph && values.ph !== prior.values.ph);
    const now = Date.now();
    return remember({version:1,capturedAt:now,expiresAt:now+TTL,values:{...(changedSubject ? {} : prior?.values),...values}});
  }
  function getTikTok({ttclid, ttp} = {}) {
    const values = get()?.values || {}, identity = {};
    [['email','em'],['phone_number','ph'],['external_id','external_id']].forEach(([key,hashKey]) => {
      if (values[hashKey]) identity[key] = values[hashKey];
    });
    Object.entries({ttclid,ttp}).forEach(([key,value]) => {
      if (typeof value === 'string' && value.trim() && value.length <= 255 && !/[\s\u0000-\u001f]/.test(value)) identity[key] = value;
    });
    return identity;
  }
  root.LVMMatchingIdentity = {get, getTikTok, remember, normalize, capture, clear, key:KEY, ttl:TTL};
})(typeof window === 'undefined' ? null : window);
